// ============================================
// SHIFTS LOGIC - Core business logic for shift constraints
// ============================================
// This module is isolated from UI and can be modified independently.
// All constraint rules are defined here.

import {
  DaySchedule,
  WeekSchedule,
  DayAvailability,
  AlenaAvailability,
  IlyaAvailability,
  ShiftAvailability,
  DayOfWeek,
} from "./types.js";

// ============================================
// CONFIGURATION - Modify these to change rules
// ============================================

// Which days have kindergarten by default (0=Sunday, 5=Friday, 6=Saturday)
const KINDERGARTEN_DAYS: Set<DayOfWeek> = new Set([0, 1, 2, 3, 4]); // Sun-Thu

// Available shifts per day of week for Alena
// Friday (5): only Morning, Saturday (6): only Evening
const ALENA_AVAILABLE_SHIFTS: Record<
  DayOfWeek,
  Set<"earlyMorning" | "morning" | "evening">
> = {
  0: new Set(["earlyMorning", "morning", "evening"]),
  1: new Set(["earlyMorning", "morning", "evening"]),
  2: new Set(["earlyMorning", "morning", "evening"]),
  3: new Set(["earlyMorning", "morning", "evening"]),
  4: new Set(["earlyMorning", "morning", "evening"]),
  5: new Set(["morning"]),
  6: new Set(["evening"]),
};

// Available shifts per day of week for Ilya
// Saturday (6): no Night shift
const ILYA_AVAILABLE_SHIFTS: Record<
  DayOfWeek,
  Set<"earlyMorning" | "evening" | "night">
> = {
  0: new Set(["earlyMorning", "evening", "night"]),
  1: new Set(["earlyMorning", "evening", "night"]),
  2: new Set(["earlyMorning", "evening", "night"]),
  3: new Set(["earlyMorning", "evening", "night"]),
  4: new Set(["earlyMorning", "evening", "night"]),
  5: new Set(["earlyMorning", "evening", "night"]),
  6: new Set(["earlyMorning", "evening"]),
};

// ============================================
// HELPER FUNCTIONS
// ============================================

function getDayOfWeek(dateStr: string): DayOfWeek {
  const date = new Date(dateStr + "T00:00:00");
  return date.getDay() as DayOfWeek;
}

function hasKindergartenDefault(dayOfWeek: DayOfWeek): boolean {
  return KINDERGARTEN_DAYS.has(dayOfWeek);
}

function blocked(reason: string, exists: boolean = true): ShiftAvailability {
  return { canWork: false, blockedReason: reason, exists };
}

function available(): ShiftAvailability {
  return { canWork: true };
}

function markBlocked<K extends string>(
  target: Record<K, ShiftAvailability>,
  key: K,
  reason: string,
): void {
  if (target[key].canWork) {
    target[key] = blocked(reason);
  }
}

// ============================================
// MAIN CONSTRAINT LOGIC
// ============================================

/**
 * Calculate availability for Alena's shifts on a given day.
 *
 * Rules when kindergarten is active:
 * - If Ilya works EarlyMorning → Alena cannot EarlyMorning
 * - If Ilya works Evening → Alena cannot Evening
 * - If Ilya works Night → Alena cannot Evening (same day)
 * - If Ilya worked Night (previous day) → Alena cannot EarlyMorning
 *
 * Rules when no kindergarten (not Saturday):
 * - Only one person can work
 * - If Ilya selected any shift → Alena cannot work
 *
 * Rules for Saturday (no kindergarten):
 * - Only Evening shift available for Alena
 * - Alena can work if Ilya only works EarlyMorning or nothing
 */
export function calculateAlenaAvailability(
  day: DaySchedule,
  prevDay: DaySchedule | null,
  nextDay: DaySchedule | null,
): AlenaAvailability {
  const dayOfWeek = getDayOfWeek(day.date);
  const alenaShifts = ALENA_AVAILABLE_SHIFTS[dayOfWeek];

  const result: AlenaAvailability = {
    earlyMorning: alenaShifts.has("earlyMorning")
      ? available()
      : blocked("Смена недоступна в этот день", false),
    morning: alenaShifts.has("morning")
      ? available()
      : blocked("Смена недоступна в этот день", false),
    evening: alenaShifts.has("evening")
      ? available()
      : blocked("Смена недоступна в этот день", false),
  };

  // Saturday special rules
  if (dayOfWeek === 6) {
    // Only Evening available, check if Ilya works Evening
    if (day.ilya.evening) {
      result.evening = blocked("Илья работает вечером");
    }
    return result;
  }

  // No kindergarten (Friday) - only one can work
  if (!day.kindergarten && dayOfWeek === 5) {
    const ilyaWorkingNormalShifts = day.ilya.earlyMorning || day.ilya.evening;
    if (ilyaWorkingNormalShifts) {
      markBlocked(
        result,
        "morning",
        "Илья уже работает, кто-то должен быть с Аглаей",
      );
    }
    return result;
  }

  // No kindergarten on other days - only one can work during the day
  if (!day.kindergarten) {
    const ilyaWorkingDay = day.ilya.earlyMorning || day.ilya.evening;
    if (ilyaWorkingDay) {
      markBlocked(
        result,
        "earlyMorning",
        "Илья уже работает, кто-то должен быть с Аглаей",
      );
      markBlocked(
        result,
        "morning",
        "Илья уже работает, кто-то должен быть с Аглаей",
      );
      markBlocked(
        result,
        "evening",
        "Илья уже работает, кто-то должен быть с Аглаей",
      );
    }
    // Night shift: Ilya leaves ~22:00, so Alena cannot work evening (nobody home with Aglaya)
    // but morning is fine (Ilya is home all day before leaving)
    if (day.ilya.night) {
      markBlocked(
        result,
        "evening",
        "Илья работает ночью, некому быть с Аглаей вечером",
      );
    }
    // Ilya worked night yesterday → earlyMorning blocked (he's not home yet ~before 6:00)
    if (prevDay?.ilya.night) {
      markBlocked(
        result,
        "earlyMorning",
        "Илья работал ночью, некому быть с Аглаей ранним утром",
      );
    }
    return result;
  }

  // Kindergarten is active - apply constraint rules

  // Ilya EarlyMorning → Alena cannot EarlyMorning
  if (day.ilya.earlyMorning) {
    markBlocked(
      result,
      "earlyMorning",
      "Илья работает раннюю смену, некому отвести Аглаю",
    );
  }

  // Ilya Evening → Alena cannot Evening
  if (day.ilya.evening) {
    markBlocked(
      result,
      "evening",
      "Илья работает вечером, некому забрать Аглаю",
    );
  }

  // Ilya Night (today) → Alena cannot Evening (today)
  if (day.ilya.night) {
    markBlocked(
      result,
      "evening",
      "Илья работает ночью, некому забрать Аглаю и быть с ней вечером",
    );
  }

  // Ilya Night (previous day) → Alena cannot EarlyMorning (today)
  if (prevDay?.ilya.night) {
    markBlocked(
      result,
      "earlyMorning",
      "Илья работал ночью, некому отвести Аглаю утром",
    );
  }

  return result;
}

/**
 * Calculate availability for Ilya's shifts on a given day.
 *
 * Rules when kindergarten is active:
 * - If Alena works EarlyMorning → Ilya cannot Night (prev day) and EarlyMorning (today)
 * - If Alena works Morning → Ilya cannot Night (prev day)
 * - If Alena works Evening → Ilya cannot Evening
 *
 * Rules when no kindergarten (not Saturday):
 * - Only one person can work
 * - If Alena selected any shift → Ilya cannot work
 *
 * Rules for Saturday (no kindergarten):
 * - No Night shift available
 * - If Alena works Evening → Ilya can only work EarlyMorning
 * - If Alena doesn't work → Ilya can work any available shift
 */
export function calculateIlyaAvailability(
  day: DaySchedule,
  prevDay: DaySchedule | null,
  nextDay: DaySchedule | null,
): IlyaAvailability {
  const dayOfWeek = getDayOfWeek(day.date);
  const ilyaShifts = ILYA_AVAILABLE_SHIFTS[dayOfWeek];

  const result: IlyaAvailability = {
    earlyMorning: ilyaShifts.has("earlyMorning")
      ? available()
      : blocked("Смена недоступна в этот день", false),
    evening: ilyaShifts.has("evening")
      ? available()
      : blocked("Смена недоступна в этот день", false),
    night: ilyaShifts.has("night")
      ? available()
      : blocked("Смена недоступна в этот день", false),
  };

  // Saturday special rules
  if (dayOfWeek === 6) {
    if (day.alena.evening) {
      // Alena works Evening → Ilya can only work EarlyMorning
      markBlocked(result, "evening", "Алёна работает вечером");
    }
    return result;
  }

  // No kindergarten (Friday) - only one can work
  if (!day.kindergarten && dayOfWeek === 5) {
    const alenaWorking =
      day.alena.earlyMorning || day.alena.morning || day.alena.evening;
    if (alenaWorking) {
      markBlocked(
        result,
        "earlyMorning",
        "Алёна уже работает, кто-то должен быть с Аглаей",
      );
      markBlocked(
        result,
        "evening",
        "Алёна уже работает, кто-то должен быть с Аглаей",
      );
    }
    return result;
  }

  // No kindergarten on other days - only one can work during the day
  if (!day.kindergarten) {
    const alenaWorkingDay =
      day.alena.earlyMorning || day.alena.morning || day.alena.evening;
    if (alenaWorkingDay) {
      markBlocked(
        result,
        "earlyMorning",
        "Алёна уже работает, кто-то должен быть с Аглаей",
      );
      markBlocked(
        result,
        "evening",
        "Алёна уже работает, кто-то должен быть с Аглаей",
      );
    }
    // Night shift only conflicts if Alena works evening (she's not home yet)
    if (day.alena.evening) {
      markBlocked(
        result,
        "night",
        "Алёна работает вечером, некому уложить Аглаю спать",
      );
    }
    return result;
  }

  // Kindergarten is active - apply constraint rules

  // Alena EarlyMorning → Ilya cannot EarlyMorning
  if (day.alena.earlyMorning) {
    markBlocked(
      result,
      "earlyMorning",
      "Алёна работает раннюю смену, некому отвести Аглаю",
    );
  }

  // Alena Evening → Ilya cannot Evening
  if (day.alena.evening) {
    markBlocked(
      result,
      "evening",
      "Алёна работает вечером, некому забрать Аглаю",
    );
  }

  // Alena Evening → Ilya cannot Night
  if (day.alena.evening) {
    markBlocked(
      result,
      "night",
      "Алёна работает вечером, некому уложить Аглаю спать",
    );
  }

  // Check if Alena needs Ilya to NOT work Night today
  // Alena EarlyMorning (next day) → Ilya cannot Night (today)
  if (nextDay?.alena.earlyMorning) {
    markBlocked(
      result,
      "night",
      "Алёна работает раннюю смену завтра, некому будет отвести Аглаю",
    );
  }

  // Alena Morning (next day) → Ilya cannot Night (today)
  // (because he needs to take Aglaya in the morning)
  // if (nextDay?.alena.morning && result.night.canWork) {
  //   result.night = blocked('Алёна работает утром завтра, некому будет отвести Аглаю');
  // }

  return result;
}

/**
 * Calculate full availability for a day, considering adjacent days.
 */
export function calculateDayAvailability(
  day: DaySchedule,
  prevDay: DaySchedule | null,
  nextDay: DaySchedule | null,
): DayAvailability {
  const dayOfWeek = getDayOfWeek(day.date);

  return {
    alena: calculateAlenaAvailability(day, prevDay, nextDay),
    ilya: calculateIlyaAvailability(day, prevDay, nextDay),
    kindergartenDefault: hasKindergartenDefault(dayOfWeek),
  };
}

/**
 * Calculate availability for entire week.
 */
export function calculateWeekAvailability(
  week: WeekSchedule,
): DayAvailability[] {
  return week.days.map((day, index) => {
    const prevDay = index > 0 ? week.days[index - 1] : null;
    const nextDay = index < week.days.length - 1 ? week.days[index + 1] : null;
    return calculateDayAvailability(day, prevDay, nextDay);
  });
}

/**
 * Create empty shifts for Alena.
 */
export function createEmptyAlenaShifts(): {
  earlyMorning: boolean;
  morning: boolean;
  evening: boolean;
} {
  return { earlyMorning: false, morning: false, evening: false };
}

/**
 * Create empty shifts for Ilya.
 */
export function createEmptyIlyaShifts(): {
  earlyMorning: boolean;
  evening: boolean;
  night: boolean;
} {
  return { earlyMorning: false, evening: false, night: false };
}

/**
 * Create a default day schedule.
 */
export function createDaySchedule(dateStr: string): DaySchedule {
  const dayOfWeek = getDayOfWeek(dateStr);
  return {
    date: dateStr,
    alena: createEmptyAlenaShifts(),
    ilya: createEmptyIlyaShifts(),
    kindergarten: hasKindergartenDefault(dayOfWeek),
  };
}

/**
 * Create a week schedule starting from a given Sunday.
 */
export function createWeekSchedule(sundayDateStr: string): WeekSchedule {
  const days: DaySchedule[] = [];
  const startDate = new Date(sundayDateStr + "T00:00:00");

  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    const dateStr = toLocalISODate(date);
    days.push(createDaySchedule(dateStr));
  }

  return {
    weekStart: sundayDateStr,
    days,
  };
}

/**
 * Format date to YYYY-MM-DD in local timezone.
 */
function toLocalISODate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Get the Sunday of the week containing a given date.
 */
export function getWeekStart(date: Date): string {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  return toLocalISODate(d);
}

/**
 * Get the Sunday of next week.
 */
export function getNextWeekStart(currentWeekStart: string): string {
  const d = new Date(currentWeekStart + "T00:00:00");
  d.setDate(d.getDate() + 7);
  return toLocalISODate(d);
}

/**
 * Get the Sunday of previous week.
 */
export function getPrevWeekStart(currentWeekStart: string): string {
  const d = new Date(currentWeekStart + "T00:00:00");
  d.setDate(d.getDate() - 7);
  return toLocalISODate(d);
}

/**
 * Format date for display (e.g., "11 Jan").
 */
export function formatDateShort(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  const day = date.getDate();
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${day} ${months[date.getMonth()]}`;
}

/**
 * Get day name for display.
 */
export function getDayName(dateStr: string): string {
  const days = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
  const date = new Date(dateStr + "T00:00:00");
  return days[date.getDay()];
}

/**
 * Validate and fix conflicts in a day schedule.
 * Returns the corrected schedule with any invalid selections removed.
 */
export function validateAndFixDay(
  day: DaySchedule,
  prevDay: DaySchedule | null,
  nextDay: DaySchedule | null,
): DaySchedule {
  const availability = calculateDayAvailability(day, prevDay, nextDay);
  const fixed = { ...day, alena: { ...day.alena }, ilya: { ...day.ilya } };

  // Remove invalid Alena shifts
  if (!availability.alena.earlyMorning.canWork)
    fixed.alena.earlyMorning = false;
  if (!availability.alena.morning.canWork) fixed.alena.morning = false;
  if (!availability.alena.evening.canWork) fixed.alena.evening = false;

  // Remove invalid Ilya shifts
  if (!availability.ilya.earlyMorning.canWork) fixed.ilya.earlyMorning = false;
  if (!availability.ilya.evening.canWork) fixed.ilya.evening = false;
  if (!availability.ilya.night.canWork) fixed.ilya.night = false;

  return fixed;
}
